import React, { useState, useCallback } from "react";
import { Copy, Check } from "lucide-react";

const TABS = [
  { key: "curl", label: "cURL" },
  { key: "javascript", label: "JavaScript" },
  { key: "python", label: "Python" },
  { key: "nodejs", label: "Node.js" },
];

// Data attributes (no digits) so number regex never matches our markup; colors applied via CSS below
const SYN = { string: ' data-syn="str"', number: ' data-syn="num"', keyword: ' data-syn="kw"', literal: ' data-syn="lit"', comment: ' data-syn="com"' };

function highlightSyntax(code) {
  let html = code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  html = html.replace(/(["'])(?:(?=(\\?))\2.)*?\1/g, '<span' + SYN.string + '>$&</span>');
  html = html.replace(/\b(\d+)\b/g, '<span' + SYN.number + '>$&</span>');
  html = html.replace(/\b(const|let|var|async|await|function|return|import|require|from|print|with|open|as)\b/g, '<span' + SYN.keyword + '>$&</span>');
  html = html.replace(/\b(true|false|null|None|undefined)\b/g, '<span' + SYN.literal + '>$&</span>');
  html = html.replace(/(\/\/.*$|#.*$)/gm, '<span' + SYN.comment + '>$&</span>');

  return html;
}

const CodeBlock = ({ codeExamples, singleCode, singleLang }) => {
  const [activeTab, setActiveTab] = useState(singleLang || "curl");
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    const text = singleCode || codeExamples?.[activeTab] || "";
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [activeTab, codeExamples, singleCode]);

  const code = singleCode || codeExamples?.[activeTab] || "";

  return (
    <div className="rounded-lg overflow-hidden border border-gray-800 bg-[#0d1117] code-block-syntax">
      <style>{`
        .code-block-syntax [data-syn="str"] { color: #86efac; }
        .code-block-syntax [data-syn="num"] { color: #fde047; }
        .code-block-syntax [data-syn="kw"] { color: #c084fc; }
        .code-block-syntax [data-syn="lit"] { color: #facc15; }
        .code-block-syntax [data-syn="com"] { color: #6b7280; }
      `}</style>
      {!singleCode && codeExamples && (
        <div className="flex items-center gap-0 border-b border-gray-800 bg-[#161b22] overflow-x-auto">
          {TABS.filter((t) => codeExamples[t.key]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-[11px] font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? "text-[#86EFAC] border-b-2 border-[#86EFAC] bg-[#0d1117]"
                  : "text-gray-400 hover:text-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors"
          title="Copy code"
        >
          {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
        </button>

        <pre className="p-4 overflow-x-auto text-[13px] leading-relaxed text-white">
          <code className="text-white" dangerouslySetInnerHTML={{ __html: highlightSyntax(code) }} />
        </pre>
      </div>
    </div>
  );
};

export default CodeBlock;
